// Copilot: PDF Merger App - complete implementation with drag-and-drop, PDF processing, and Firebase integration
// Features: file management, drag-and-drop reordering, header/footer/page numbers, Firebase storage upload

import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { PDFDocument, StandardFonts, rgb, degrees } from 'pdf-lib';
import Footer from './Footer';

// Types
interface FileItem {
  id: string;
  file: File;
}

interface PdfMergerAppProps {
  onNavigate: (page: string) => void;
}

interface Status {
  message?: string;
  error?: string;
}

// Install button component
const InstallButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowInstall(false);
    }
  };

  if (!showInstall) return null;

  return (
    <button
      onClick={handleInstall}
      className="bg-blue-500 hover:bg-blue-600 text-white px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center space-x-1 sm:space-x-2"
    >
      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
      <span className="hidden sm:inline">Install App</span>
      <span className="sm:hidden">Install</span>
    </button>
  );
};

// Status display component
const StatusBox: React.FC<{ status: Status }> = ({ status }) => {
  if (!status.message && !status.error) return null;

  return (
    <div className={`p-4 rounded-md mb-4 ${status.error
        ? 'bg-red-50 border border-red-200 text-red-700'
        : 'bg-green-50 border border-green-200 text-green-700'
      }`}>
      {status.error || status.message}
    </div>
  );
};

const PdfMergerApp: React.FC<PdfMergerAppProps> = ({ onNavigate }) => {
  // State management
  const [files, setFiles] = useState<FileItem[]>([]);
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [addPageNumbers, setAddPageNumbers] = useState(false);
  const [includeFileNames, setIncludeFileNames] = useState(false);
  const [mergedPdfUrl, setMergedPdfUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<Status>({});

  // Font customization state
  const [headerFont, setHeaderFont] = useState('Helvetica');
  const [headerFontSize, setHeaderFontSize] = useState(12);
  const [headerColor, setHeaderColor] = useState('#000000');
  const [headerBold, setHeaderBold] = useState(false);
  const [headerItalic, setHeaderItalic] = useState(false);
  const [headerAlignment, setHeaderAlignment] = useState('left');

  const [footerFont, setFooterFont] = useState('Helvetica');
  const [footerFontSize, setFooterFontSize] = useState(10);
  const [footerColor, setFooterColor] = useState('#000000');
  const [footerBold, setFooterBold] = useState(false);
  const [footerItalic, setFooterItalic] = useState(false);
  const [footerAlignment, setFooterAlignment] = useState('left');

  // File handling
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;

    const allPdfFiles = Array.from(fileList).filter(file => file.type === 'application/pdf');
    const validPdfFiles = allPdfFiles.filter(file => file.size > 0);
    const emptyPdfFiles = allPdfFiles.filter(file => file.size === 0);

    const newFileItems: FileItem[] = validPdfFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file
    }));

    setFiles(prev => [...prev, ...newFileItems]);
    
    if (emptyPdfFiles.length > 0) {
      setStatus({ 
        error: `Skipped ${emptyPdfFiles.length} empty file(s) (0 bytes)`,
        message: undefined
      });
    } else {
      setStatus({ message: `Added ${validPdfFiles.length} PDF file(s)`, error: undefined });
    }
  };

  // Drag and drop handling
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newFiles = Array.from(files);
    const [reorderedItem] = newFiles.splice(result.source.index, 1);
    newFiles.splice(result.destination.index, 0, reorderedItem);

    setFiles(newFiles);
  };

  // Remove file
  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(item => item.id !== id));
  };

  // Remove all files
  const removeAllFiles = () => {
    setFiles([]);
    setMergedPdfUrl(null);
    setStatus({ message: 'All files removed' });
  };

  // Auto-cleanup after download
  const handleDownload = () => {
    if (mergedPdfUrl) {
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = mergedPdfUrl;
      link.download = 'merged.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up after download
      setTimeout(() => {
        removeAllFiles();
        setStatus({ message: 'PDF downloaded and files cleaned up!' });
      }, 1000); // Small delay to ensure download starts
    }
  };

  // PDF merge logic
  const handleMergePDFs = async () => {
    if (files.length === 0) {
      setStatus({ error: 'Please add at least one PDF file' });
      return;
    }

    setIsProcessing(true);
    setMergedPdfUrl(null);
    setStatus({});

    try {
      // Create new PDF document
      const mergedPdfDoc = await PDFDocument.create();

      // Process each file in order
      let validFilesCount = 0;
      const pageToFileMap: string[] = [];

      for (const fileItem of files) {
        if (fileItem.file.size === 0) continue;
        
        const arrayBuffer = await fileItem.file.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const copiedPages = await mergedPdfDoc.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach(page => {
          mergedPdfDoc.addPage(page);
          pageToFileMap.push(fileItem.file.name);
        });
        validFilesCount++;
      }

      if (validFilesCount === 0) {
        throw new Error("No valid, non-empty PDFs found to merge.");
      }

      // Apply headers, footers, page numbers, and file names if requested
      if (headerText || footerText || addPageNumbers || includeFileNames) {
        // Helper function to get font
        const getFontStyle = async (fontName: string, bold: boolean, italic: boolean) => {
          switch (fontName) {
            case 'Times':
              if (bold && italic) return await mergedPdfDoc.embedFont(StandardFonts.TimesRomanBoldItalic);
              if (bold) return await mergedPdfDoc.embedFont(StandardFonts.TimesRomanBold);
              if (italic) return await mergedPdfDoc.embedFont(StandardFonts.TimesRomanItalic);
              return await mergedPdfDoc.embedFont(StandardFonts.TimesRoman);

            case 'Courier':
              if (bold && italic) return await mergedPdfDoc.embedFont(StandardFonts.CourierBoldOblique);
              if (bold) return await mergedPdfDoc.embedFont(StandardFonts.CourierBold);
              if (italic) return await mergedPdfDoc.embedFont(StandardFonts.CourierOblique);
              return await mergedPdfDoc.embedFont(StandardFonts.Courier);

            case 'Symbol':
              // Symbol font doesn't support bold/italic, and is for symbols only
              return await mergedPdfDoc.embedFont(StandardFonts.Symbol);

            case 'ZapfDingbats':
              // ZapfDingbats font doesn't support bold/italic, and is for symbols only
              return await mergedPdfDoc.embedFont(StandardFonts.ZapfDingbats);

            default: // Helvetica
              if (bold && italic) return await mergedPdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
              if (bold) return await mergedPdfDoc.embedFont(StandardFonts.HelveticaBold);
              if (italic) return await mergedPdfDoc.embedFont(StandardFonts.HelveticaOblique);
              return await mergedPdfDoc.embedFont(StandardFonts.Helvetica);
          }
        };

        // Helper function to parse hex color
        const hexToRgb = (hex: string) => {
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result ? {
            r: parseInt(result[1], 16) / 255,
            g: parseInt(result[2], 16) / 255,
            b: parseInt(result[3], 16) / 255
          } : { r: 0, g: 0, b: 0 };
        };

        // Helper function to calculate text position based on alignment
        const getTextPosition = (text: string, font: any, fontSize: number, pageWidth: number, alignment: string) => {
          const textWidth = font.widthOfTextAtSize(text, fontSize);
          switch (alignment) {
            case 'center':
              return (pageWidth - textWidth) / 2;
            case 'right':
              return pageWidth - textWidth - 30;
            default: // left
              return 30;
          }
        };

        const pages = mergedPdfDoc.getPages();
        const totalPages = pages.length;

        // Get fonts for header and footer
        const headerFontStyle = headerText ? await getFontStyle(headerFont, headerBold, headerItalic) : null;
        const footerFontStyle = footerText ? await getFontStyle(footerFont, footerBold, footerItalic) : null;
        const defaultFont = await mergedPdfDoc.embedFont(StandardFonts.Helvetica);

        // Get colors
        const headerRgb = hexToRgb(headerColor);
        const footerRgb = hexToRgb(footerColor);

        pages.forEach((page, index) => {
          const { width, height } = page.getSize();
          const pageNumber = index + 1;
          const fileName = pageToFileMap[index];

          // Calculate available space for footer text (accounting for page numbers)
          const pageNumbersWidth = addPageNumbers ? 120 : 0; // Reserve space for page numbers
          const availableFooterWidth = width - pageNumbersWidth - 60; // 60px total margins

          if (addPageNumbers) {
            const pageText = `Page ${pageNumber} of ${totalPages}`;
            page.drawText(pageText, {
              x: width - 100,
              y: 30,
              size: 10,
              font: footerFontStyle || defaultFont,
              color: rgb(0, 0, 0)
            });
          }

          if (headerText && headerFontStyle) {
            const headerX = getTextPosition(headerText, headerFontStyle, headerFontSize, width, headerAlignment);
            page.drawText(headerText, {
              x: headerX,
              y: height - 40,
              size: headerFontSize,
              font: headerFontStyle,
              color: rgb(headerRgb.r, headerRgb.g, headerRgb.b)
            });
          }

          if (footerText && footerFontStyle) {
            // Adjust footer positioning to avoid page numbers
            let footerX;
            if (footerAlignment === 'right' && addPageNumbers) {
              // If right-aligned and page numbers exist, position footer to the left of page numbers
              const footerTextWidth = footerFontStyle.widthOfTextAtSize(footerText, footerFontSize);
              footerX = width - pageNumbersWidth - footerTextWidth - 20; // 20px gap
            } else if (footerAlignment === 'center' && addPageNumbers) {
              // If centered and page numbers exist, center within available space
              const footerTextWidth = footerFontStyle.widthOfTextAtSize(footerText, footerFontSize);
              footerX = (availableFooterWidth - footerTextWidth) / 2 + 30;
            } else {
              // Use normal positioning for left alignment or when no page numbers
              footerX = getTextPosition(footerText, footerFontStyle, footerFontSize, addPageNumbers ? availableFooterWidth + 30 : width, footerAlignment);
            }

            page.drawText(footerText, {
              x: Math.max(30, footerX), // Ensure minimum left margin
              y: 30,
              size: footerFontSize,
              font: footerFontStyle,
              color: rgb(footerRgb.r, footerRgb.g, footerRgb.b)
            });
          }

          if (includeFileNames && fileName) {
            // Draw file name vertically on the left margin
            page.drawText(fileName, {
              x: 15,
              y: 30, // Start from bottom
              size: 8,
              font: defaultFont,
              color: rgb(0.5, 0.5, 0.5), // Gray color so it's unobtrusive
              rotate: degrees(90),
            });
          }
        });
      }

      // Save the merged PDF
      const mergedPdfBytes = await mergedPdfDoc.save();
      const blob = new Blob([new Uint8Array(mergedPdfBytes)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      setMergedPdfUrl(url);
      setStatus({ message: 'PDF merged successfully!' });

    } catch (error: any) {
      console.error('PDF merge error:', error);
      setStatus({ error: `Failed to merge PDFs: ${error.message}` });
    } finally {
      setIsProcessing(false);
    }
  };



  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-gray-100 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <img
                src="/its_my_merge.png"
                alt="its my merge"
                className="h-8 sm:h-12 w-auto"
              />
              <div className="cursor-pointer" onClick={() => onNavigate('landing')}>
                <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-orange-600 bg-clip-text text-transparent hover:scale-105 transition-transform">
                  its my merge
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm hidden sm:block">Merge, customize, and save your PDF files</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <a
                href="https://www.buymeacoffee.com/Itsmymerge"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-2 sm:px-3 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center space-x-1"
              >
                <span>☕</span>
                <span className="hidden sm:inline">Buy me a coffee</span>
              </a>
              <InstallButton />
            </div>
          </div>
        </div>

        <StatusBox status={status} />

        {/* File Upload */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Upload PDF Files</h2>
          <input
            type="file"
            multiple
            accept="application/pdf"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-2 sm:file:mr-4 file:py-2 sm:file:py-3 file:px-3 sm:file:px-6 file:rounded-lg file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/50 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/70 file:transition-colors"
          />
        </div>

        {/* File List with Drag and Drop */}
        {files.length > 0 && (
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-gray-100 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2 sm:mb-0">
                PDF Files ({files.length})
              </h2>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center">
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/30 px-2 sm:px-3 py-1 rounded-full text-center">
                  💡 Drag files to reorder them
                </div>
                <button
                  onClick={removeAllFiles}
                  className="bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors"
                >
                  🗑️ Remove All
                </button>
              </div>
            </div>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="files">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {files.map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`p-3 sm:p-4 border rounded-lg mb-2 flex justify-between items-center ${snapshot.isDragging ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                              }`}
                          >
                            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                              <div className="text-gray-400 text-sm sm:text-base">⋮⋮</div>
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 dark:text-white text-sm sm:text-base truncate">{item.file.name}</div>
                                <div className="text-xs sm:text-sm text-gray-500">
                                  {formatFileSize(item.file.size)}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => removeFile(item.id)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs sm:text-sm ml-2 flex-shrink-0"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}

        {/* PDF Options */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">PDF Options</h2>

          {/* Header Section */}
          <div className="space-y-4 mb-6">
            <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 border-b dark:border-gray-700 pb-2">Header Settings</h3>
            <div>
              <label htmlFor="header" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Header Text
              </label>
              <input
                id="header"
                type="text"
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                className="w-full px-3 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter header text (optional)"
              />
            </div>

            {headerText && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Font</label>
                  <select
                    value={headerFont}
                    onChange={(e) => setHeaderFont(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Helvetica">Helvetica</option>
                    <option value="Times">Times Roman</option>
                    <option value="Courier">Courier</option>
                    <option value="Symbol">Symbol</option>
                    <option value="ZapfDingbats">Zapf Dingbats</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                  <input
                    type="number"
                    min="8"
                    max="48"
                    value={headerFontSize}
                    onChange={(e) => setHeaderFontSize(parseInt(e.target.value) || 12)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="color"
                    value={headerColor}
                    onChange={(e) => setHeaderColor(e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
                  <select
                    value={headerAlignment}
                    onChange={(e) => setHeaderAlignment(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>
            )}

            {headerText && (
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={headerBold}
                    onChange={(e) => setHeaderBold(e.target.checked)}
                    disabled={headerFont === 'Symbol' || headerFont === 'ZapfDingbats'}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className={`ml-2 text-sm text-gray-700 font-bold ${(headerFont === 'Symbol' || headerFont === 'ZapfDingbats') ? 'opacity-50' : ''}`}>Bold</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={headerItalic}
                    onChange={(e) => setHeaderItalic(e.target.checked)}
                    disabled={headerFont === 'Symbol' || headerFont === 'ZapfDingbats'}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className={`ml-2 text-sm text-gray-700 italic ${(headerFont === 'Symbol' || headerFont === 'ZapfDingbats') ? 'opacity-50' : ''}`}>Italic</span>
                </label>
                {(headerFont === 'Symbol' || headerFont === 'ZapfDingbats') && (
                  <p className="text-xs text-amber-600 w-full mt-1">
                    ⚠️ {headerFont} font is for symbols only - bold/italic not available
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer Section */}
          <div className="space-y-4 mb-6">
            <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 border-b dark:border-gray-700 pb-2">Footer Settings</h3>
            <div>
              <label htmlFor="footer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Footer Text
              </label>
              <input
                id="footer"
                type="text"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value)}
                className="w-full px-3 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter footer text (optional)"
              />
            </div>

            {footerText && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Font</label>
                  <select
                    value={footerFont}
                    onChange={(e) => setFooterFont(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Helvetica">Helvetica</option>
                    <option value="Times">Times Roman</option>
                    <option value="Courier">Courier</option>
                    <option value="Symbol">Symbol</option>
                    <option value="ZapfDingbats">Zapf Dingbats</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                  <input
                    type="number"
                    min="8"
                    max="48"
                    value={footerFontSize}
                    onChange={(e) => setFooterFontSize(parseInt(e.target.value) || 10)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="color"
                    value={footerColor}
                    onChange={(e) => setFooterColor(e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
                  <select
                    value={footerAlignment}
                    onChange={(e) => setFooterAlignment(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>
            )}

            {footerText && (
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={footerBold}
                    onChange={(e) => setFooterBold(e.target.checked)}
                    disabled={footerFont === 'Symbol' || footerFont === 'ZapfDingbats'}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className={`ml-2 text-sm text-gray-700 font-bold ${(footerFont === 'Symbol' || footerFont === 'ZapfDingbats') ? 'opacity-50' : ''}`}>Bold</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={footerItalic}
                    onChange={(e) => setFooterItalic(e.target.checked)}
                    disabled={footerFont === 'Symbol' || footerFont === 'ZapfDingbats'}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className={`ml-2 text-sm text-gray-700 italic ${(footerFont === 'Symbol' || footerFont === 'ZapfDingbats') ? 'opacity-50' : ''}`}>Italic</span>
                </label>
                {(footerFont === 'Symbol' || footerFont === 'ZapfDingbats') && (
                  <p className="text-xs text-amber-600 w-full mt-1">
                    ⚠️ {footerFont} font is for symbols only - bold/italic not available
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Page Numbers */}
          <div className="border-t dark:border-gray-700 pt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={addPageNumbers}
                onChange={(e) => setAddPageNumbers(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Add page numbers</span>
            </label>
          </div>

          {/* File Names */}
          <div className="pt-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeFileNames}
                onChange={(e) => setIncludeFileNames(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Include original file names on pages</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleMergePDFs}
              disabled={isProcessing || files.length === 0}
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-lg hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 transform hover:scale-[1.02]"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Merge PDFs'
              )}
            </button>

            {mergedPdfUrl && (
              <button
                onClick={handleDownload}
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-lg hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 text-center font-medium transition-all duration-200 transform hover:scale-[1.02] flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download & Clean Up
              </button>
            )}
          </div>
        </div>
      </div>

      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default PdfMergerApp;